/**
 * 사람들은 이렇게 다녀왔어요 — 여행지를 고른 뒤 참고할 코스.
 *
 * 코스가 없으면 **섹션째 숨깁니다.** 빈 영역은 고장처럼 보입니다.
 * 지금 우리 코스는 0건이라 대부분의 지역에서 아무것도 그리지 않습니다 —
 * 그건 미완성이 아니라 설계된 동작입니다. 여행을 짜는 데도 지장이 없습니다
 * (장소를 직접 고르는 길이 항상 있습니다).
 *
 * 카드에 "몇 명이 따라했는지" 를 넣은 이유
 *   별점만으로는 표본 크기를 알 수 없습니다. 별 5개(후기 2건)보다
 *   별 4.6(34명 따라함)이 더 믿을 만합니다. 그리고 그 수는 조회수가 아니라
 *   실제로 따라간 수입니다.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CardRail, PlaceCard, Section, SectionHeader, StarRating } from 'components/ds';
import { useTravelCourses } from 'hooks/useTravelCourse';
import styles from './CourseBrowseSection.module.css';

interface Props {
    /** 여행지 — 이 지역 코스만 보여 줍니다 */
    regionId?: string | null;
    /** 아직 여행지를 안 골랐으면 부르지 않습니다 */
    enabled?: boolean;
}

export const CourseBrowseSection: React.FC<Props> = ({ regionId, enabled = true }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { courses } = useTravelCourses(regionId, 'popular', enabled && !!regionId);

    /* 없으면 섹션째 숨깁니다 */
    if (courses.length === 0) return null;

    return (
        <Section divided>
            <SectionHeader title={t('course.browseTitle')} sub={t('course.browseSub')} />
            <CardRail>
                {courses.map(c => (
                    <div key={c.courseId} className={styles.cell}>
                        <PlaceCard
                            wide
                            imageUrl={c.thumbnailUrl}
                            name={c.title}
                            meta={[
                                c.days && c.days > 1
                                    ? t('course.nights', { n: c.days - 1, d: c.days })
                                    : t('course.oneDay'),
                                t('course.placeCount', { n: c.placeCount || 0 }),
                            ].join(' · ')}
                            evidence={[
                                c.creatorNickname || '',
                                (c.copiedCount || 0) > 0
                                    ? t('course.copiedCount', { n: c.copiedCount })
                                    : '',
                            ].filter(Boolean).join(' · ')}
                            onClick={() => navigate(`/travel/course/${c.courseId}`)}
                        />
                        {c.ratingAvg != null && (
                            <div className={styles.rating}>
                                <StarRating
                                    value={Number(c.ratingAvg)}
                                    count={c.ratingCount || 0}
                                    size={12}
                                    source="CREW"
                                />
                            </div>
                        )}
                    </div>
                ))}
            </CardRail>
        </Section>
    );
};
